import Link from 'components/ui/AppLink';
import { Box, Paper, Typography } from '@mui/material';
import React from 'react';

import { baseUrls } from '../../../utils/baseUrls.utils';
import {
    ChannelSubscriptionType,
    ChannelSubscriptionWithPaymentInfo,
    ChannelTypes,
} from '../../../shared/types';
import { UpdateCardButton } from './UpdateCardButton';
import { capitalizeFirstLetter } from '../../../utils/string.utils';
import { config } from '../../../config';
import { getChannelPublisherUrl } from '../../../utils/channel.utils';
import {
    isNonChargeChannelSubscriptionType,
    isAccountChannelSubscriptionType,
    isExternalBillingChannelSubscriptionType,
    isTrialChannelSubscriptionType,
} from '../../../utils/channelSubscriptions.utils';
import { formatDisplayDate } from '../../../utils/date.utils';
import { ChannelUnsubscribe } from './ChannelUnsubscribe';

import { Grid } from '@mui/material';
interface ChannelSubscriptionCardProps {
    subscription: ChannelSubscriptionWithPaymentInfo;
    onUpdate: () => void;
}

const ChannelSubscriptionCard = ({
    subscription,
    onUpdate,
}: ChannelSubscriptionCardProps) => {
    const { channel } = subscription;
    if (!channel) return <></>;
    const channelUrl = `${baseUrls.browse.channelMain}/${channel._id}`;
    const price = `$${!subscription.pricePerMonth ? channel.price : subscription.pricePerMonth}/month*`;

    const showNextPaymentDate = () => {
        if (!subscription.nextPayment) return;

        return formatDisplayDate(subscription.nextPayment, false) ?? '';
    };

    const capitalizePaymentDetails = () => {
        if (subscription.paymentMethod.type === 'Card') {
            return capitalizeFirstLetter(subscription.paymentMethod.details);
        }

        return subscription.paymentMethod.details;
    };

    const getAutoCancelDate = () =>
        formatDisplayDate(subscription?.cancelAt, false) ?? '';
    const isFreeChannelSubscriptions =
        subscription.channel.type !== ChannelTypes.premium;
    const disableSubscriptionOptions = subscription.cancelAtPeriodEnd;
    const rebatedAccount =
        isAccountChannelSubscriptionType(subscription.subscriptionType) &&
        subscription.rebate;

    return (
        <Paper sx={{ p: 4, bgcolor: 'background.paper' }}>
            <Grid container spacing={4}>
                <Grid size={12}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Link to={channelUrl}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'text.primary',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {channel.name}
                                </Typography>
                            </Link>
                        </Grid>
                        <Grid size={12}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    '& a': { fontSize: 'inherit' },
                                }}
                            >
                                Published by{' '}
                                <Link to={getChannelPublisherUrl(channel)}>
                                    {channel.owner.name}
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <Grid container spacing={1}>
                        {!isFreeChannelSubscriptions && (
                            <Grid size={12}>
                                <Typography
                                    component="span"
                                    variant="body1"
                                    sx={{
                                        color: 'text.secondary',

                                        textDecoration:
                                            (isNonChargeChannelSubscriptionType(
                                                subscription.subscriptionType
                                            ) &&
                                                !subscription.pricePerMonth) ||
                                            rebatedAccount ||
                                            isTrialChannelSubscriptionType(
                                                subscription.subscriptionType
                                            )
                                                ? 'line-through'
                                                : undefined,
                                    }}
                                >
                                    <b>{price}</b>
                                </Typography>{' '}
                                {isTrialChannelSubscriptionType(
                                    subscription.subscriptionType
                                ) ? (
                                    <Typography
                                        component="span"
                                        variant="body1"
                                    >
                                        <strong>Free trial</strong>
                                    </Typography>
                                ) : isNonChargeChannelSubscriptionType(
                                      subscription.subscriptionType
                                  ) ? (
                                    !subscription.pricePerMonth ? (
                                        <Typography
                                            component="span"
                                            variant="body1"
                                        >
                                            <b>Free subscription</b>
                                        </Typography>
                                    ) : (
                                        <></>
                                    )
                                ) : rebatedAccount ? (
                                    <>
                                        <Typography
                                            component="span"
                                            variant="body1"
                                        >
                                            <b>Rebated</b>
                                        </Typography>{' '}
                                        {showNextPaymentDate() && (
                                            <Typography
                                                component="span"
                                                variant="body1"
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                next payment{' '}
                                                {showNextPaymentDate()}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    !subscription.cancelAtPeriodEnd &&
                                    showNextPaymentDate() &&
                                    !isExternalBillingChannelSubscriptionType(
                                        subscription.subscriptionType
                                    ) && (
                                        <Typography
                                            component="span"
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            next payment {showNextPaymentDate()}
                                        </Typography>
                                    )
                                )}
                            </Grid>
                        )}
                        {!isTrialChannelSubscriptionType(
                            subscription.subscriptionType
                        ) && (
                            <Grid size={12}>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    {isNonChargeChannelSubscriptionType(
                                        subscription.subscriptionType
                                    ) ? (
                                        !subscription.pricePerMonth ? (
                                            <>
                                                You have been granted free
                                                access to this Channel
                                            </>
                                        ) : (
                                            <> </>
                                        )
                                    ) : (
                                        <>
                                            <b>
                                                {
                                                    subscription.paymentMethod
                                                        .type
                                                }
                                            </b>{' '}
                                            {capitalizePaymentDetails()}
                                        </>
                                    )}
                                </Typography>
                            </Grid>
                        )}
                        <Grid size={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {config.stripePaymentsEnabled &&
                                    subscription.subscriptionType ===
                                        ChannelSubscriptionType.Stripe &&
                                    !disableSubscriptionOptions && (
                                        <>
                                            <UpdateCardButton
                                                subscriptionId={
                                                    subscription.subscriptionId
                                                }
                                                onStripeCardUpdated={onUpdate}
                                            />
                                            <Box
                                                sx={{
                                                    mx: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 4,
                                                        height: 4,
                                                        bgcolor: 'divider',
                                                        borderRadius: '50%',
                                                    }}
                                                />
                                            </Box>
                                        </>
                                    )}
                                <ChannelUnsubscribe
                                    subscription={subscription}
                                    onUpdate={onUpdate}
                                />
                            </Box>
                        </Grid>
                        {(subscription.cancelAtPeriodEnd ||
                            subscription.cancelAt) && (
                            <Grid size={12}>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    {isTrialChannelSubscriptionType(
                                        subscription.subscriptionType
                                    )
                                        ? 'Free trial ending on '
                                        : 'Subscription expires on '}
                                    {getAutoCancelDate()}
                                </Typography>
                            </Grid>
                        )}
                        {!isFreeChannelSubscriptions && (
                            <Grid size={12}>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    <strong>
                                        *Price excludes applicable sales taxes
                                    </strong>
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ChannelSubscriptionCard;
